import React, { useState } from 'react';
import { Box, Chip, Link, ListSubheader, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { BUCKET_COLORS } from '../../OnlineJournal/constants';
import { daysLeftLabel, fmtDay, fmtQty } from '../../OnlineJournal/format';
import { RemainsPharmacy, StockExpiry } from '../remainsApi';
import { LatLng, NearestExpiry, Origin, bucketKeyOf, mapLinks, routeLinks } from '../remainsCalc';
import { EMPTY_TEXT, OPEN_COLORS, emptySx, openKey, smallChipSx } from '../remainsStyles';

/** Значение или серое «не указано» */
export const Value: React.FC<{ text?: string | null; sx?: object }> = ({ text, sx }) => (
  text
    ? <Typography sx={{ fontSize: 12.5, color: '#475569', ...sx }} noWrap title={text}>{text}</Typography>
    : <Typography sx={emptySx}>{EMPTY_TEXT}</Typography>
);

/** Ближайший срок годности (по журналу сроков, без серий): дата в цвете корзины, рядом - просроченное количество */
export const ExpiryCell: React.FC<{ nearest: NearestExpiry | null; expiredQty: number }> = ({ nearest, expiredQty }) => {
  if (!nearest && !expiredQty) return <Typography sx={emptySx}>не указан</Typography>;
  const c = BUCKET_COLORS[nearest ? bucketKeyOf(nearest) : 'expired'];
  const tip = [
    nearest ? `${fmtDay(nearest.date)}: ${daysLeftLabel(nearest.daysLeft)}, ${fmtQty(nearest.qty)} уп. с этим сроком` : 'Неистёкших сроков в журнале нет',
    expiredQty ? `В журнале с истёкшим сроком: ${fmtQty(expiredQty)} уп.` : ''
  ].filter(Boolean).join('. ');
  return (
    <Tooltip title={tip} arrow>
      <Box sx={{ minWidth: 0 }}>
        <Chip size="small" label={nearest ? fmtDay(nearest.date) : 'просрочено'} sx={{ ...smallChipSx, color: c.color, bgcolor: c.bg }} />
        {nearest && expiredQty > 0 && (
          <Typography sx={{ fontSize: 11, color: BUCKET_COLORS.expired.color, mt: 0.25 }} noWrap>просрочено {fmtQty(expiredQty)} уп.</Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export const StockExpiryCell: React.FC<{ expiry: StockExpiry | null }> = ({ expiry }) => (
  <ExpiryCell nearest={expiry ? expiry.nearest : null} expiredQty={expiry ? expiry.expiredQty : 0} />
);

/** «Открыта до 22:00» / «Закрыта, откроется в 09:00»; режим работы не указан или не распознан - ничего */
export function openLabel(p: RemainsPharmacy): string | null {
  if (p.open === true) return p.closesAt ? `Открыта до ${p.closesAt}` : 'Открыта круглосуточно';
  if (p.open === false) return p.opensAt ? `Закрыта, откроется в ${p.opensAt}` : 'Закрыта';
  return null;
}

export const OpenChip: React.FC<{ pharmacy: RemainsPharmacy }> = ({ pharmacy }) => {
  const label = openLabel(pharmacy);
  if (!label) return null;
  const c = OPEN_COLORS[openKey(pharmacy.open)];
  return <Chip size="small" label={label} sx={{ ...smallChipSx, color: c.color, bgcolor: c.bg, maxWidth: '100%' }} />;
};

/** Режим работы из профиля аптеки и состояние сейчас */
export const HoursCell: React.FC<{ pharmacy: RemainsPharmacy }> = ({ pharmacy }) => (
  <Box sx={{ minWidth: 0 }}>
    <Value text={pharmacy.hours} />
    {pharmacy.open !== null && <Box sx={{ mt: 0.25 }}><OpenChip pharmacy={pharmacy} /></Box>}
  </Box>
);

/** Название аптеки: «Аптека №N» и имя в 1С, склад - отдельной отметкой */
export const PharmacyName: React.FC<{ pharmacy: RemainsPharmacy }> = ({ pharmacy }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }} noWrap title={pharmacy.name}>{pharmacy.name}</Typography>
      {pharmacy.kind === 'warehouse' && (
        <Chip size="small" label="склад" sx={{ ...smallChipSx, height: 18, fontSize: 10.5, color: '#7c3aed', bgcolor: 'rgba(124,58,237,0.08)' }} />
      )}
    </Box>
    {pharmacy.name1c && pharmacy.name1c !== pharmacy.name && (
      <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap title={pharmacy.name1c}>{pharmacy.name1c}</Typography>
    )}
  </Box>
);

/** Открыть точку во внешних картах */
export const MapLinks: React.FC<{ point: LatLng }> = ({ point }) => (
  <Box component="span" sx={{ display: 'inline-flex', gap: 1 }}>
    {mapLinks(point).map(l => (
      <Link key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12, fontWeight: 600 }}>
        {l.label}
      </Link>
    ))}
  </Box>
);

/**
 * Ссылка с меню «Маршрут от точки отсчёта» в 2GIS, Яндекс и Google. Меню рисуется в портале, но события React
 * всплывают по дереву компонентов - без stopPropagation клик по пункту открыл бы ещё и карточку аптеки из строки таблицы
 */
export const RouteLink: React.FC<{ from: Origin; to: LatLng; label: string; sx?: object }> = ({ from, to, label, sx }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  return (
    <>
      <Link
        component="button" type="button" underline="hover"
        onClick={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'baseline', ...sx }}
      >
        {label}
      </Link>
      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={close} onClick={e => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ListSubheader sx={{ lineHeight: '32px', fontSize: 12, color: '#64748b', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Маршрут от «{from.label}»
        </ListSubheader>
        {routeLinks(from, to).map(l => (
          <MenuItem key={l.label} component="a" href={l.href} target="_blank" rel="noopener noreferrer" onClick={close} sx={{ fontSize: 13 }}>
            {l.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
